import { ErrorMessage } from 'views/components';
import { PositionPicker } from 'views/pickers';
import PropTypes from 'prop-types';
import moment from 'moment';
import { patientActions } from 'core/Patient';
import { useTranslation } from 'react-i18next';
import {
    ButtonFooter,
    Checkbox,
    Container,
    DateSelect,
    ExpandButton,
    FormGroup,
    IconButton,
    Modal
} from 'mol-ui';
import { Col, Row } from 'reactstrap';
import React, { useCallback, useEffect, useState } from 'react';
import {
    dateToObject,
    objectToDate,
    validationSchema
} from 'shared/utils';
import {
    useArray,
    useDispatch,
    useSelector
} from 'hooks';

const DEFAULT_POSITION = {
    end_date: dateToObject(moment()),
    position_id: undefined,
    start_date: dateToObject(moment())
};

const PositionModal = ({ active, patientId, onToggle }) => {
    
    const { t } = useTranslation();
    const dispatch = useDispatch();

    const {
        positions,
        positionsLoading
    } = useSelector('patient');
    const [currentPositions, setCurrentPositions, {
        push: pushPosition,
        removeByIndex: removePosition,
        replaceValues
    }] = useArray([], { type: 'object' });
    const [error, setError] = useState('');

    useEffect(() => {
        if (active && patientId) {
            dispatch(patientActions.getPatientPositions(patientId));
        }
    }, [active, patientId, dispatch]);

    useEffect(() => {
        setCurrentPositions(positions.map(position => ({
            ...position,
            end_date: position.end_date ? dateToObject(moment(position.end_date)) : undefined,
            position_id: position.position.id,
            start_date: dateToObject(moment(position.start_date)),
            unfinished: !position.end_date
        })));
    }, [positions]);

    const checkPositionErrors = positions => positions.some(({ end_date, start_date, position_id, unfinished }) => {
        const startDate = moment(start_date);
        const endDate = moment(end_date);
        return (!unfinished && startDate.isAfter(endDate)) || !position_id;
    });

    const validate = useCallback(() => {
        const { errors, isValid } = validationSchema({
            general: [!checkPositionErrors(currentPositions), t('Las fechas de fin no pueden ser menores a las de inicio y los cargos deben estar completos')]
        });
        setError(errors?.general ?? '');
        return isValid;
    }, [currentPositions]);

    const onSubmit = () => {
        if (!validate()) return;
        dispatch(patientActions.updatePatientPositions({
            patient_id: patientId,
            positions: currentPositions.map(position => ({
                end_date: position.end_date && !position.unfinished ? objectToDate(position.end_date).format('YYYY-MM-DD') : undefined,
                id: position.id ?? undefined,
                position_id: position.position_id,
                start_date: objectToDate(position.start_date).format('YYYY-MM-DD')
            }))
        }));
    };

    return (
        <Modal
            active={ active }
            loading={ positionsLoading }
            width="90%"
            onToggle={ onToggle }
        >
            <Container alignItems="center" width="100%">
                {
                    currentPositions.map((position, index) => (
                        <Container key={ index } width="100%">
                            <Row>
                                <Col md="3" xs="12">
                                    <FormGroup label={ t('Desde') } margin="0px 0px 10px 0px">
                                        <DateSelect
                                            value={ position.start_date }
                                            onChange={ date => replaceValues({ start_date: date }, 'index', index) }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3" xs="12">
                                    <FormGroup label={ t('Hasta') } margin="0px 0px 10px 0px">
                                        <DateSelect
                                            disabled={ position.unfinished }
                                            value={ position.unfinished ? undefined : position.end_date }
                                            onChange={ date => replaceValues({ end_date: date }, 'index', index) }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="3" xs="8">
                                    <FormGroup label={ t('Cargo') } margin="0px 0px 10px 0px">
                                        <PositionPicker
                                            value={ position.position_id }
                                            onChange={ id => replaceValues({ position_id: id }, 'index', index) }
                                        />
                                    </FormGroup>
                                </Col>
                                <Col md="1" xs="4">
                                    <Container height="100%" justifyContent="center" width="100%">
                                        <ExpandButton
                                            circle
                                            danger
                                            icon="delete"
                                            margin="5px 0px 0px 0px"
                                            size={ 40 }
                                            onClick={ () => removePosition(index) }
                                        />
                                    </Container>
                                </Col>
                                <Col md="2" xs="12">
                                    <Container height="100%" justifyContent="center" width="100%">
                                        <Checkbox
                                            checked={ position.unfinished }
                                            label={ t('Sin fecha de finalización') }
                                            onClick={ value => replaceValues({ unfinished: value }, 'index', index) }
                                        />
                                    </Container>
                                </Col>
                            </Row>
                        </Container>
                    ))
                }
                <ErrorMessage relative error={ error } />
                <IconButton
                    fontSize="14px"
                    icon="add"
                    label={ t('Nuevo cargo') }
                    margin="10px 0px 20px 0px"
                    onClick={ () => pushPosition({ ...DEFAULT_POSITION }) }
                />
                <ButtonFooter margin="0px 0px 0px 0px">
                    <ExpandButton
                        danger
                        caption={ t('Cancelar') }
                        size={ 80 }
                        onClick={ onToggle }
                    />
                    <ExpandButton
                        caption={ t('Guardar') }
                        size={ 80 }
                        onClick={ onSubmit }
                    />
                </ButtonFooter>
            </Container>
        </Modal>
    );
};

PositionModal.propTypes = {
    active: PropTypes.bool,
    onToggle: PropTypes.func,
    patientId: PropTypes.string
};

export default PositionModal;
